import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Zap } from 'lucide-react'
import { documentsApi, type DocumentPreview } from '../api'
import ChandraScanButton from '../components/ChandraScanButton'
import SplitScreenPreview from '../components/SplitScreenPreview'
import { useToastStore } from '../store/toast'

export default function AdvancedPreview() {
  const { documentId } = useParams<{ documentId: string }>()
  const addToast = useToastStore((state) => state.addToast)
  const [preview, setPreview] = useState<DocumentPreview | null>(null)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [loading, setLoading] = useState(true)

  const loadPreview = useCallback(() => {
    if (!documentId) return
    setLoading(true)
    documentsApi.preview(documentId, 25)
      .then((res) => {
        setPreview(res.data)
        setSelectedIds(res.data.columns.map((column) => column.id))
      })
      .catch((err) => addToast({ type: 'error', message: err.response?.data?.error || 'Could not load advanced preview.' }))
      .finally(() => setLoading(false))
  }, [documentId, addToast])

  useEffect(() => {
    loadPreview()
  }, [loadPreview])

  const toggle = (id: string) => {
    setSelectedIds((ids) => ids.includes(id) ? ids.filter((item) => item !== id) : [...ids, id])
  }

  if (loading) return <div className="h-full grid place-items-center text-gray-500">Loading split-screen preview...</div>
  if (!documentId || !preview) return null

  return (
    <div className="h-[calc(100vh-8rem)] min-h-[640px] flex flex-col bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to={`/documents/${documentId}/preview`} className="p-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div>
            <h1 className="font-semibold text-gray-950">Split-screen OCR review</h1>
            <p className="text-sm text-gray-600">{preview.document.original_filename}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-2 rounded-lg bg-purple-50 border border-purple-200 px-3 py-2 text-sm text-purple-700">
            <Zap className="w-4 h-4" />
            Page 1 advanced scan test mode
          </div>
          <ChandraScanButton documentId={documentId} pageRange="1" onScanComplete={loadPreview} />
        </div>
      </div>

      <div className="min-h-0 flex-1">
        <SplitScreenPreview
          documentId={documentId}
          pdfUrl={documentsApi.fileUrl(documentId)}
          tables={preview.tables}
          columns={preview.columns}
          selectedIds={selectedIds}
          toggle={toggle}
        />
      </div>
    </div>
  )
}
